import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Activity, Pill, AlertCircle, History, ClipboardList, Shield,
  ChevronDown, Clock, Heart, Thermometer, User
} from 'lucide-react';

export default function EncounterLeftPanel({ patient, pastEncounters, prepPacket, prepCollapsed, setPrepCollapsed, navigate, encounter }) {
  const ni = encounter?.nurse_intake || {};
  const vitals = ni.vitals || {};
  const hasNurseData = ni.primary_reason || Object.keys(vitals).length > 0;

  return (
    <ScrollArea className="h-full">
      <div className="p-3 space-y-3">
        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Patient Context</h3>

        {/* Nurse Handoff — top of left panel */}
        {hasNurseData && (
          <Card className="shadow-none border-l-2 border-l-emerald-400" data-testid="nurse-handoff-panel">
            <CardContent className="p-2.5 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <User className="w-3 h-3 text-emerald-500" />
                <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Nurse Handoff</span>
                {ni.completed_at && (
                  <span className="text-[8px] text-muted-foreground ml-auto">
                    {new Date(ni.completed_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                  </span>
                )}
              </div>
              {/* Chief complaint */}
              {ni.primary_reason && (
                <div>
                  <span className="text-[9px] text-muted-foreground">CC: </span>
                  <span className="text-xs font-medium">{ni.primary_reason}</span>
                </div>
              )}
              {ni.secondary_reason && (
                <div>
                  <span className="text-[9px] text-muted-foreground">Also: </span>
                  <span className="text-[11px]">{ni.secondary_reason}</span>
                </div>
              )}
              {ni.free_note && (
                <p className="text-[10px] text-muted-foreground italic">"{ni.free_note}"</p>
              )}
              {/* Vitals grid */}
              {Object.keys(vitals).length > 0 && (
                <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-1 pt-1 border-t border-emerald-100">
                  {vitals.bp_systolic && <div className="text-[10px]"><Heart className="w-2.5 h-2.5 inline text-red-400 mr-0.5" /><span className="font-mono font-medium">{vitals.bp_systolic}/{vitals.bp_diastolic}</span></div>}
                  {vitals.pulse && <div className="text-[10px]"><Activity className="w-2.5 h-2.5 inline text-blue-400 mr-0.5" /><span className="font-mono">{vitals.pulse}</span></div>}
                  {vitals.temp && <div className="text-[10px]"><Thermometer className="w-2.5 h-2.5 inline text-orange-400 mr-0.5" /><span className="font-mono">{vitals.temp}</span></div>}
                  {vitals.resp_rate && <div className="text-[10px]"><span className="text-muted-foreground">RR</span> <span className="font-mono">{vitals.resp_rate}</span></div>}
                  {vitals.o2_sat && <div className="text-[10px]"><span className="text-muted-foreground">O2</span> <span className="font-mono">{vitals.o2_sat}%</span></div>}
                  {vitals.pain_score !== undefined && vitals.pain_score !== null && <div className="text-[10px]"><span className="text-muted-foreground">Pain</span> <span className="font-mono">{vitals.pain_score}</span></div>}
                  {vitals.weight && <div className="text-[10px]"><span className="text-muted-foreground">Wt</span> <span className="font-mono">{vitals.weight}</span></div>}
                  {vitals.height && <div className="text-[10px]"><span className="text-muted-foreground">Ht</span> <span className="font-mono">{vitals.height}</span></div>}
                </div>
              )}
              {/* Visit type + room */}
              <div className="flex items-center gap-2 text-[9px] text-muted-foreground pt-0.5">
                {ni.actual_visit_type && <Badge variant="outline" className="text-[8px] h-4">{ni.actual_visit_type}</Badge>}
                {encounter?.room && <span>Room: {encounter.room}</span>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Prep */}
        {prepPacket && prepPacket.flags?.length > 0 && (
          <Card className="shadow-none border-l-2 border-l-primary/30" data-testid="todays-prep-panel">
            <CardContent className="p-2.5">
              <button className="flex items-center gap-1.5 w-full text-left mb-1"
                onClick={() => setPrepCollapsed(!prepCollapsed)}>
                <ClipboardList className="w-3 h-3 text-primary" />
                <span className="text-[10px] font-semibold text-primary">Today's Prep</span>
                <ChevronDown className={`w-3 h-3 text-muted-foreground ml-auto transition-transform ${prepCollapsed ? '-rotate-90' : ''}`} />
              </button>
              {!prepCollapsed && (
                <div className="space-y-1.5 mt-1">
                  {prepPacket.flags.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[10px]">
                      {f.type === 'warning' ? <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" />
                        : f.type === 'action' ? <Clock className="w-3 h-3 text-blue-500 shrink-0" />
                        : <Activity className="w-3 h-3 text-slate-400 shrink-0" />}
                      <span>{f.text}</span>
                    </div>
                  ))}
                  {prepPacket.care_gaps?.length > 0 && (
                    <div className="text-[10px] text-amber-600 pt-1 border-t">
                      {prepPacket.care_gaps.length} care gap{prepPacket.care_gaps.length > 1 ? 's' : ''}
                    </div>
                  )}
                  {prepPacket.medication_summary?.high_risk_meds?.length > 0 && (
                    <div className="text-[10px] text-red-600 flex items-center gap-1">
                      <Shield className="w-3 h-3" /> High-risk: {prepPacket.medication_summary.high_risk_meds.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Active Problems */}
        {patient && (
          <>
            <Card className="shadow-none">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                  <Activity className="w-3 h-3" /> Active Problems
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1">
                {(patient.problem_list || []).filter(p => p.status === 'active').map((p, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${p.classification === 'chronic' ? 'bg-blue-400' : 'bg-amber-400'}`} />
                    <span className="text-xs">{p.condition_name}</span>
                    {p.icd_code && <span className="text-[10px] text-muted-foreground font-mono">{p.icd_code}</span>}
                  </div>
                ))}
                {(patient.problem_list || []).filter(p => p.status === 'active').length === 0 && (
                  <p className="text-xs text-muted-foreground">No active problems</p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader className="p-3 pb-1"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><Pill className="w-3 h-3" /> Medications</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-1">
                {(patient.medications || []).filter(m => m.status === 'active').map((m, i) => (
                  <div key={i} className="text-xs flex items-center gap-1">
                    <span>{m.name}</span>
                    {m.dose && <span className="text-muted-foreground">{m.dose}</span>}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader className="p-3 pb-1"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Allergies</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-1">
                {(patient.allergies || []).map((a, i) => (
                  <div key={i} className="text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-amber-500" />
                    <span>{a.name}</span>
                    <span className="text-muted-foreground">({a.severity})</span>
                  </div>
                ))}
                {(!patient.allergies || patient.allergies.length === 0) && <p className="text-xs text-muted-foreground">NKDA</p>}
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                  <History className="w-3 h-3" /> Past Visits
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                {pastEncounters.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No previous visits</p>
                ) : (
                  pastEncounters.map((pe, i) => (
                    <div key={i} className="border-b last:border-0 pb-2 last:pb-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] tabular-nums font-medium">{pe.date?.split('T')[0]}</span>
                        <span className="text-[10px] text-muted-foreground">{pe.provider_name}</span>
                      </div>
                      {pe.encounter_outputs?.visit_summary?.summary_short && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                          {pe.encounter_outputs.visit_summary.summary_short}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </ScrollArea>
  );
}
